/**
 * FaqAccordion — accessible accordion for the paywall FAQ
 * (issue #434 acceptance criterion #5).
 *
 * Pattern follows WAI-ARIA Authoring Practices for the
 * "Accordion (Sections With Show/Hide)" pattern. Each row is
 * a `<button>` inside a `<h3>` so the heading semantics stay
 * intact; the panel uses `aria-labelledby` to point back at
 * the button's id.
 *
 * Keyboard support (per AC #5):
 *   - Tab/Shift-Tab moves focus between accordion buttons.
 *   - Enter/Space toggles the focused row.
 *   - Down/Up arrows move focus to the next/previous button
 *     (wraps at the ends).
 *
 * State is owned by the parent so the panel can default one
 * row open on mount (we open the first).
 */
import { useId, useRef, type ReactElement } from 'react';
import type { FaqItem } from '../lib/paywall/planContent';

interface Props {
  items: readonly FaqItem[];
  /** Indices of rows currently expanded. */
  openIndices: ReadonlySet<number>;
  onToggle: (index: number) => void;
}

export function FaqAccordion({
  items,
  openIndices,
  onToggle,
}: Props): ReactElement {
  const baseId = useId();
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  function focusButton(index: number): void {
    const next = buttonRefs.current[index];
    if (next) next.focus();
  }

  function handleKeyDown(
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ): void {
    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        focusButton((index + 1) % items.length);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        focusButton((index - 1 + items.length) % items.length);
        break;
      }
      case 'Home': {
        event.preventDefault();
        focusButton(0);
        break;
      }
      case 'End': {
        event.preventDefault();
        focusButton(items.length - 1);
        break;
      }
      default:
        // Enter/Space fire the native click — no extra handler needed.
        break;
    }
  }

  return (
    <section className="faq" aria-label="Frequently asked questions">
      <h2 className="onboarding-eyebrow">Questions procurement asks</h2>
      <div className="faq-list">
        {items.map((item, index) => {
          const buttonId = `${baseId}-faq-${index}-button`;
          const panelId = `${baseId}-faq-${index}-panel`;
          const expanded = openIndices.has(index);
          return (
            <div
              className={'faq-row' + (expanded ? ' faq-row--open' : '')}
              key={`${baseId}-faq-${index}`}
            >
              <h3 className="faq-question-heading">
                <button
                  type="button"
                  id={buttonId}
                  ref={(el) => {
                    buttonRefs.current[index] = el;
                  }}
                  className="faq-question-button"
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  onClick={() => onToggle(index)}
                  onKeyDown={(event) => handleKeyDown(event, index)}
                >
                  <span className="faq-question-text">{item.question}</span>
                  <span
                    className="faq-chevron"
                    aria-hidden="true"
                  >
                    {expanded ? '\u2212' : '+'}
                  </span>
                </button>
              </h3>
              <div
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                hidden={!expanded}
                className="faq-answer"
              >
                <p>{item.answer}</p>
                <a
                  className="link-button"
                  href={item.learnMoreHref}
                  data-testid={`faq-learn-more-${index}`}
                >
                  Learn more \u2192 {item.learnMoreLabel}
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}